// ── 4 Modelle ── die Flow-Modelle: Zustand, Rumpf und Vorschläge (rein).
//
// Flow-Modelle laufen über einen eigenen Requesty-Key mit eigenem Monatslimit
// (docs/11 8d) — nie über den Key der Agenten. Die Plattform legt fest, welche
// es gibt; wer sie benutzen darf, stellt der Tenant-Admin je Rolle ein.

import type { FlowModelKind, FlowModelSetting, Tenant } from "../../api/types";
import type { SelectOptions } from "../../ui/select-menu";

export const FLOW_KIND: SelectOptions = [["llm", "Sprachmodell"], ["decision", "Entscheidung"]];

/**
 * Entscheidungsmodelle bei Requesty — über denselben Endpunkt, mit einem
 * eigenen Antwortformat (das regelt die Instanz). `openai/gpt-6-luna` gehört
 * nicht dazu: Es geht bei Requesty nur als Sprachmodell.
 */
export const DECISION_MODEL_SUGGESTIONS: readonly string[] = [
  "typesafe/jev-latest",
  "cloudflare/clef",
  "perplexity/pplx-decider-v1-27b",
];

/** Eine Zeile im Formular. `rid` hält React die Zeile fest; `key` ist der Schlüssel der Instanz (leer = neu). */
export interface FlowModelRow extends FlowModelSetting {
  rid: string;
}

export interface FlowsState {
  f_enabled: boolean;
  f_budget: string;
  f_models: FlowModelRow[];
}

export interface FlowsBody {
  enabled: boolean;
  budget_usd: number;
  models: FlowModelSetting[];
}

export function flowsFromTenant(t: Tenant): FlowsState {
  const f = t.models.flows;
  return {
    f_enabled: f.enabled,
    f_budget: String(f.budget_usd ?? ""),
    f_models: f.models.map((m, i) => ({ ...m, rid: m.key || `saved-${i}` })),
  };
}

/** Eine neue, leere Zeile am Ende — mit einer `rid`, die es in der Liste noch nicht gibt. */
export function addFlowModel(rows: readonly FlowModelRow[], kind: FlowModelKind = "llm"): FlowModelRow[] {
  const used = new Set(rows.map((r) => r.rid));
  let n = rows.length + 1;
  while (used.has(`new-${n}`)) n += 1;
  return [...rows, { rid: `new-${n}`, key: "", name: "", kind, model: "", description: "" }];
}

export function updateFlowModel(rows: readonly FlowModelRow[], rid: string, patch: Partial<FlowModelSetting>): FlowModelRow[] {
  return rows.map((r) => (r.rid === rid ? { ...r, ...patch } : r));
}

export function removeFlowModel(rows: readonly FlowModelRow[], rid: string): FlowModelRow[] {
  return rows.filter((r) => r.rid !== rid);
}

/**
 * Der Rumpf: ganz leere Zeilen fallen weg (wie auf dem Server — sonst zeigten
 * die Fehler `flows.models.<i>` auf die falsche Zeile). Der Schlüssel geht mit,
 * damit die Instanz ihr Modell beim Umbenennen wiedererkennt.
 */
export function flowsToBody(s: FlowsState): FlowsBody {
  return {
    enabled: s.f_enabled,
    // `Number("")` ist 0 — der Server meldet dann „Muss größer als 0 sein".
    budget_usd: Number(s.f_budget),
    models: s.f_models
      .map((r) => ({ key: r.key, name: r.name.trim(), kind: r.kind, model: r.model.trim(), description: r.description.trim() }))
      .filter((m) => m.name || m.model),
  };
}

/** Der Index einer Zeile im Rumpf (für die Fehler des Servers) — `-1` für eine leere Zeile. */
export function bodyIndexOf(rows: readonly FlowModelRow[], rid: string): number {
  let i = 0;
  for (const r of rows) {
    const counted = Boolean(r.name.trim() || r.model.trim());
    if (r.rid === rid) return counted ? i : -1;
    if (counted) i += 1;
  }
  return -1;
}

/** Was „Modell-ID" vorschlägt: Entscheidung die drei von Requesty, Sprachmodell der Katalog. */
export function modelSuggestions(kind: FlowModelKind, catalog: readonly string[]): readonly string[] {
  return kind === "decision" ? DECISION_MODEL_SUGGESTIONS : catalog;
}
